'use client'

import Link from 'next/link'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/patterns/form-field'
import { forgotPasswordSchema, type ForgotPasswordInput } from '@/features/auth/schemas'
import { useForgotPassword } from '@/features/auth/hooks/use-forgot-password'

export function ForgotPasswordForm() {
  const { mutate: forgotPassword, isPending, isSuccess } = useForgotPassword()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  function onSubmit(data: ForgotPasswordInput) {
    forgotPassword(data)
  }

  if (isSuccess) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Email enviado</CardTitle>
          <CardDescription>
            Se o email estiver cadastrado, você receberá um link para redefinir sua senha.
          </CardDescription>
        </CardHeader>
        <CardFooter className="justify-center">
          <Link
            href="/login"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary-brand)] hover:underline"
          >
            <ArrowLeft className="size-4" />
            Voltar ao login
          </Link>
        </CardFooter>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Esqueceu a senha?</CardTitle>
        <CardDescription>
          Informe seu email para receber um link de recuperacao
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="flex flex-col gap-4">
          <FormField label="Email" htmlFor="email" error={errors.email?.message} required>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              autoComplete="email"
              {...register('email')}
            />
          </FormField>

          <Button type="submit" disabled={isPending} className="w-full text-white font-semibold">
            {isPending && <Loader2 className="animate-spin" />}
            Enviar link
          </Button>
        </CardContent>

        <CardFooter className="justify-center">
          <Link
            href="/login"
            className="inline-flex items-center gap-1 text-sm text-[var(--color-primary-brand)] hover:underline"
          >
            <ArrowLeft className="size-4" />
            Voltar ao login
          </Link>
        </CardFooter>
      </form>
    </Card>
  )
}
